interface GeolocationResult {
  success: boolean;
  location: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  error?: string;
}

interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  fallbackToIP?: boolean;
  useCache?: boolean;
}

class GeolocationService {
  private cache: Map<string, { location: string; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
  private readonly DEFAULT_TIMEOUT = 10000; // 10 seconds

  /**
   * Get user's current location with proper error handling and privacy controls
   */
  async getCurrentLocation(options: GeolocationOptions = {}): Promise<GeolocationResult> {
    const {
      enableHighAccuracy = false,
      timeout = this.DEFAULT_TIMEOUT,
      maximumAge = 60000, // 1 minute
      fallbackToIP = true,
      useCache = true
    } = options;

    try {
      // Check cache first if enabled
      if (useCache) {
        const cached = this.getCachedLocation();
        if (cached) {
          return { success: true, location: cached };
        }
      }

      // Check if geolocation is available
      if (!('geolocation' in navigator)) {
        console.warn('Geolocation not supported by this browser');
        return this.fallbackLocation(fallbackToIP);
      }

      // Request location with proper error handling
      const position = await this.getPositionAsync({
        enableHighAccuracy,
        timeout,
        maximumAge
      });

      // Reverse geocode the coordinates
      const location = await this.reverseGeocode(
        position.coords.latitude,
        position.coords.longitude
      );

      // Cache the result
      if (useCache) {
        this.setCachedLocation(location);
      }

      return {
        success: true,
        location,
        coordinates: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }
      };

    } catch (error) {
      console.warn('Geolocation request failed:', error);
      return this.fallbackLocation(fallbackToIP);
    }
  }

  /**
   * Get location without requesting browser permission (IP-based fallback only)
   */
  async getLocationWithoutPermission(): Promise<GeolocationResult> {
    try {
      const location = await this.getIPLocation();
      return { success: true, location };
    } catch (error) {
      return { 
        success: false, 
        location: 'Unknown location', 
        error: error instanceof Error ? error.message : 'Failed to get location' 
      };
    }
  }

  /**
   * Check if user has already granted location permission
   */
  async hasLocationPermission(): Promise<boolean> {
    if (!('permissions' in navigator)) {
      return false;
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      return permission.state === 'granted';
    } catch (error) {
      console.warn('Could not check location permission:', error);
      return false;
    }
  }

  /**
   * Clear location cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  // Private methods

  private async getPositionAsync(options: PositionOptions): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => reject(this.formatGeolocationError(error)),
        options
      );
    });
  }

  private async reverseGeocode(lat: number, lon: number): Promise<string> {
    try {
      // Use a free reverse geocoding service
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
      );

      if (!response.ok) {
        throw new Error('Reverse geocoding failed');
      }

      const data = await response.json();
      
      // Format the location nicely
      const city = data.city || data.locality || data.principalSubdivision;
      const country = data.countryName || data.countryCode;
      
      if (city && country) {
        return `${city}, ${country}`;
      } else if (country) {
        return country;
      } else {
        return `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
      }

    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      // Return approximate coordinates as fallback
      return `${lat.toFixed(1)}°N, ${lon.toFixed(1)}°W`;
    }
  }

  private async getIPLocation(): Promise<string> {
    try {
      // Try multiple IP geolocation services as fallback
      const services = [
        'https://ipapi.co/json/',
        'https://ipinfo.io/json',
        'https://api.ipify.org?format=json'
      ];

      for (const service of services) {
        try {
          const response = await fetch(service);
          const data = await response.json();
          
          if (data.city && data.country) {
            return `${data.city}, ${data.country}`;
          } else if (data.country_name) {
            return data.country_name;
          }
        } catch (error) {
          console.warn(`IP service ${service} failed:`, error);
          continue;
        }
      }

      throw new Error('All IP geolocation services failed');
    } catch (error) {
      throw new Error('Unable to determine location');
    }
  }

  private getCachedLocation(): string | null {
    const cached = this.cache.get('location');
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.location;
    }
    return null;
  }

  private setCachedLocation(location: string): void {
    this.cache.set('location', { location, timestamp: Date.now() });
  }

  private async fallbackLocation(fallbackToIP: boolean): Promise<GeolocationResult> {
    if (fallbackToIP) {
      try {
        const location = await this.getIPLocation();
        return { success: true, location };
      } catch (error) {
        return { 
          success: false, 
          location: 'Unknown location', 
          error: error instanceof Error ? error.message : 'Failed to get location' 
        };
      }
    }

    return { success: false, location: 'Unknown location', error: 'Location access denied' };
  }

  private formatGeolocationError(error: GeolocationPositionError): Error {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return new Error('Location access denied by user');
      case error.POSITION_UNAVAILABLE:
        return new Error('Location information unavailable');
      case error.TIMEOUT:
        return new Error('Location request timed out');
      default:
        return new Error('Unknown geolocation error');
    }
  }
}

// Export singleton instance
export const geolocationService = new GeolocationService();
export type { GeolocationResult, GeolocationOptions }; 